"use client";

import { forwardRef, useImperativeHandle, useRef } from "react";
import { useResettableDateInput } from "@/lib/resettableDate";
import { cn } from "@/components/ui/cn";

const controlBase =
  "w-full rounded-lg border bg-surface text-base text-foreground placeholder:text-muted/60 " +
  "transition-colors focus:border-accent focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]/30 " +
  "disabled:cursor-not-allowed disabled:opacity-60";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

/**
 * Mobile keyboard for a numeric control. `type="number"` alone still opens the
 * full alphanumeric keyboard on iOS, so every number field gets an explicit
 * `inputMode`: the digits-only pad when `step` is a whole number, the decimal
 * pad (with a separator key) otherwise. Fields that don't declare a `step` are
 * treated as fractional — offering a separator that isn't needed is harmless,
 * withholding one the operator needs is not.
 */
function numericInputMode(
  type: React.InputHTMLAttributes<HTMLInputElement>["type"],
  step: React.InputHTMLAttributes<HTMLInputElement>["step"]
): React.HTMLAttributes<HTMLInputElement>["inputMode"] | undefined {
  if (type !== "number") return undefined;
  return step !== undefined && /^\d+$/.test(String(step)) ? "numeric" : "decimal";
}

const PICKER_TYPES = new Set(["date", "datetime-local", "time", "month", "week"]);

/**
 * Opens the native picker for date-like inputs on any click, not only on the
 * calendar icon (desktop Chrome/Firefox otherwise just focus a segment).
 * `showPicker` throws outside a user gesture or where unsupported — iOS then
 * opens the wheel natively from the tap itself.
 */
export function openNativePicker(el: HTMLInputElement): void {
  if (el.disabled || el.readOnly) return;
  try {
    el.showPicker?.();
  } catch {
    // Native tap behaviour still applies.
  }
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ invalid, className, onWheel, onClick, placeholder, ...rest }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);
    useResettableDateInput(inputRef, rest.type);
    const isPicker = rest.type !== undefined && PICKER_TYPES.has(rest.type);
    // Date inputs ignore `placeholder` and show the locale mask (or nothing on
    // iOS), so an empty one hides its text and overlays the placeholder.
    const overlay = isPicker && placeholder !== undefined && !rest.value;
    const input = (
      <input
        ref={inputRef}
        inputMode={rest.inputMode ?? numericInputMode(rest.type, rest.step)}
        placeholder={isPicker ? undefined : placeholder}
        className={cn(
          controlBase,
          "h-10 px-3",
          isPicker && "cursor-pointer appearance-none",
          overlay &&
            "peer text-transparent! focus:text-foreground! [&:not(:focus)::-webkit-date-and-time-value]:text-transparent [&:not(:focus)::-webkit-datetime-edit]:text-transparent",
          invalid ? "border-danger" : "border-border",
          className
        )}
        onClick={(e) => {
          if (isPicker) openNativePicker(e.currentTarget);
          onClick?.(e);
        }}
        onWheel={(e) => {
          // Scrolling over a focused number input would otherwise change its
          // value — blur it so the wheel just scrolls the page.
          if (rest.type === "number" && e.currentTarget === document.activeElement) {
            e.currentTarget.blur();
          }
          onWheel?.(e);
        }}
        {...rest}
      />
    );
    if (!overlay) return input;
    return (
      <span className="relative block">
        {input}
        <span className="pointer-events-none absolute inset-y-0 left-3 right-9 flex items-center truncate text-sm text-muted peer-focus:hidden">
          {placeholder}
        </span>
      </span>
    );
  }
);
Input.displayName = "Input";

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ invalid, className, ...rest }, ref) => {
    return (
      <textarea
        ref={ref}
        className={cn(
          controlBase,
          "min-h-[4.5rem] px-3 py-2",
          invalid ? "border-danger" : "border-border",
          className
        )}
        {...rest}
      />
    );
  }
);
Textarea.displayName = "Textarea";

export interface SelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ invalid, className, children, ...rest }, ref) => {
    return (
      <select
        ref={ref}
        className={cn(
          controlBase,
          "h-10 px-3",
          invalid ? "border-danger" : "border-border",
          className
        )}
        {...rest}
      >
        {children}
      </select>
    );
  }
);
Select.displayName = "Select";

export interface FieldProps {
  label: React.ReactNode;
  /** Renders the required asterisk next to the label. */
  required?: boolean;
  error?: React.ReactNode;
  hint?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

/** Label + control + error/hint wrapper for form fields. */
export function Field({
  label,
  required,
  error,
  hint,
  className,
  children,
}: FieldProps) {
  return (
    <div className={cn("space-y-1.5", className)}>
      <label className="block text-sm font-medium text-foreground">
        {label}
        {required && <span className="ml-0.5 text-danger">*</span>}
      </label>
      {children}
      {error ? (
        <p className="text-xs text-danger">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted">{hint}</p>
      ) : null}
    </div>
  );
}
